import React from 'react'
import { Check, X } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import AIButton from '../ai/AIButton'

interface TrueFalseStatement {
  id: string
  content: string
  correctAnswer?: boolean
}

interface TrueFalseQuestionProps {
  question: {
    id: string
    question_lead?: string  // DS schema uses this for main question
    statements?: TrueFalseStatement[]
    lua_chon?: string[]
    statement_a?: string
    statement_b?: string
    statement_c?: string
    statement_d?: string
    answer_a?: any  // Can be 0/1, "0"/"1", true/false, "true"/"false"
    answer_b?: any
    answer_c?: any
    answer_d?: any
    dap_an_dung?: any
    solution_a?: string
    solution_b?: string
    solution_c?: string
    solution_d?: string
    hinh_anh?: string
    statementOrder?: string[]  // Thứ tự hiển thị (đảo mệnh đề) — ['c','a','d','b']. Giữ nguyên
    // 'key' gắn với statement_a/b/c/d gốc (giống option_id TN) nên đảo hiển thị KHÔNG làm
    // lệch chấm điểm (nộp bài vẫn dùng key gốc, không dùng vị trí hiển thị).
  }
  answers: Record<string, any>
  onAnswerChange: (questionId: string, answer: any) => void
  showResults?: boolean
  isFullscreen?: boolean
}

const TrueFalseQuestion: React.FC<TrueFalseQuestionProps> = ({
  question,
  answers,
  onAnswerChange,
  showResults = false,
  isFullscreen = false
}) => {
  // Clean statement content: bỏ dấu sao và normalize newline → space (inline như pmaker liverender)
  const cleanStatementContent = (content: string): string => {
    if (!content) return content
    let cleaned = content.replace(/^\s*\*\s*/, '').replace(/\s*\*\s*$/, '').trim()
    // Câu hỏi từ pmaker compile nhiều đoạn Word → '\n' phải thành space để công thức inline
    cleaned = cleaned.replace(/\n+/g, ' ')
    return cleaned
  }

  // Generate statements from database data
  const generateStatements = (): TrueFalseStatement[] => {
    console.log('🔍 TrueFalseQuestion - Raw question data:', {
      id: question.id,
      type: (question as any).type,
      question_lead: question.question_lead,
      statement_a: question.statement_a,
      statement_b: question.statement_b,
      statement_c: question.statement_c,
      statement_d: question.statement_d,
      answer_a: question.answer_a,
      answer_b: question.answer_b,
      answer_c: question.answer_c,
      answer_d: question.answer_d
    })

    // If statements are explicitly provided, use them
    if (question.statements && question.statements.length > 0) {
      console.log('✅ Using explicit statements:', question.statements)
      return question.statements
    }

    // DS Schema: Luôn lấy 4 phát biểu từ statement_a/b/c/d — nếu có statementOrder (đảo mệnh
    // đề) thì đổi thứ tự MẢNG này thôi, mỗi phần tử vẫn giữ nguyên 'key' gắn với cột gốc.
    const byKey: Record<string, { key: string; statement?: string; answer?: any; solution?: string }> = {
      a: { key: 'a', statement: question.statement_a, answer: question.answer_a, solution: question.solution_a },
      b: { key: 'b', statement: question.statement_b, answer: question.answer_b, solution: question.solution_b },
      c: { key: 'c', statement: question.statement_c, answer: question.answer_c, solution: question.solution_c },
      d: { key: 'd', statement: question.statement_d, answer: question.answer_d, solution: question.solution_d }
    }
    const order = (question.statementOrder && question.statementOrder.length === 4) ? question.statementOrder : ['a', 'b', 'c', 'd']
    const dsStatements = order.map(k => byKey[k]).filter(Boolean)

    console.log('🔍 DS Statements raw:', dsStatements)

    // Chỉ lấy những phát biểu có nội dung
    const validStatements = dsStatements.filter(item => item.statement && item.statement.trim())
    console.log('✅ Valid statements:', validStatements)

    // DS Schema: Luôn ưu tiên statement_a/b/c/d nếu có
    if (validStatements.length > 0) {
      console.log('🎯 Using DS Schema statements');
      return validStatements.map((item, index) => {
        // Parse correct answer từ dấu sao (*) trong statement hoặc answer_a/b/c/d
        let correctAnswer = false
        
        // Priority 1: Check for asterisk (*) in statement content
        if (item.statement && typeof item.statement === 'string') {
          // Check if statement starts with asterisk (*)
          const hasAsterisk = /^\s*\*\s*/.test(item.statement)
          if (hasAsterisk) {
            correctAnswer = true
            console.log(`📝 Statement ${item.key} marked as correct with asterisk (*): "${item.statement}"`)
          }
        }
        
        // Priority 2: Fallback to answer_a/b/c/d if no asterisk found
        if (!correctAnswer && item.answer !== undefined && item.answer !== null) {
          // DS Schema: answer có thể là 0/1, "0"/"1", true/false, "true"/"false", "Đúng"/"Sai"
          if (item.answer === 1 || item.answer === '1' || item.answer === true || item.answer === 'true' || item.answer === 'Đúng') {
            correctAnswer = true
          } else if (item.answer === 0 || item.answer === '0' || item.answer === false || item.answer === 'false' || item.answer === 'Sai') {
            correctAnswer = false
          }
          console.log(`📝 Statement ${item.key} correct answer from answer field: ${correctAnswer} (from ${item.answer})`)
        }

        return {
          id: `${question.id}_statement_${item.key}`,
          content: cleanStatementContent(item.statement),
          correctAnswer
        }
      })
    }

    console.log('⚠️ No DS statements found, falling back to lua_chon')
    // Priority 2: Use lua_chon from database as statements
    if (question.lua_chon && question.lua_chon.length >= 4) {
      return question.lua_chon.slice(0, 4).map((statement, index) => {
        // Parse correct answer từ dấu sao (*) trong statement hoặc dap_an_dung
        let correctAnswer = false

        // Priority 1: Check for asterisk (*) in statement content
        if (statement && typeof statement === 'string') {
          // Check if statement starts with asterisk (*)
          const hasAsterisk = /^\s*\*\s*/.test(statement)
          if (hasAsterisk) {
            correctAnswer = true
            console.log(`📝 lua_chon[${index}] marked as correct with asterisk (*): "${statement}"`)
          }
        }
        
        // Priority 2: Fallback to dap_an_dung if no asterisk found
        if (!correctAnswer && question.dap_an_dung) {
          if (typeof question.dap_an_dung === 'string') {
            // Handle format like "ĐSSS" or "TFFF" or "1010"
            const answerStr = question.dap_an_dung.toUpperCase()
            if (answerStr.length > index) {
              const char = answerStr[index]
              correctAnswer = char === 'Đ' || char === 'T' || char === '1' || char === 'TRUE'
            }
          } else if (Array.isArray(question.dap_an_dung)) {
            // Handle array format like [true, false, false, false]
            correctAnswer = Boolean(question.dap_an_dung[index])
          } else if (typeof question.dap_an_dung === 'object') {
            // Handle object format
            const key = `statement_${index}`
            correctAnswer = Boolean(question.dap_an_dung[key])
          }
          console.log(`📝 lua_chon[${index}] correct answer from dap_an_dung: ${correctAnswer} (from ${question.dap_an_dung})`)
        }

        return {
          id: `${question.id}_statement_${index}`,
          content: cleanStatementContent(statement),
          correctAnswer
        }
      })
    }

    // Fallback: Không có statements, trả về mảng rỗng
    // Vì câu dẫn chính đã được hiển thị ở phần trên
    return []
  }

  const statements = generateStatements()

  // Get current answers for this question (object containing answers for each statement)
  const currentAnswers = answers[question.id] || {}

  const handleStatementAnswer = (statementId: string, answer: boolean) => {
    const newAnswers = {
      ...currentAnswers,
      [statementId]: answer
    }
    onAnswerChange(question.id, newAnswers)
  }

  const getButtonStyle = (statementId: string, answerValue: boolean, isCorrect?: boolean) => {
    const currentAnswer = currentAnswers[statementId]
    const isSelected = currentAnswer === answerValue

    if (showResults) {
      // Show results mode - highlight correct/incorrect answers
      if (isSelected) {
        if (isCorrect) {
          return 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-500/30'
        } else {
          return 'bg-gradient-to-r from-red-500 to-red-600 text-white border-red-600 shadow-lg shadow-red-500/30'
        }
      } else if (isCorrect !== undefined) {
        // Show correct answer even if not selected
        if ((answerValue && isCorrect) || (!answerValue && !isCorrect)) {
          return 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border-emerald-400 shadow-md'
        }
      }
      return 'bg-gray-100 text-gray-500 border-gray-300'
    } else {
      // Normal quiz mode - Compact circular buttons
      if (isSelected) {
        return 'bg-gradient-to-r from-orange-500 to-orange-600 text-white border-orange-600 shadow-xl shadow-orange-500/40'
      }
      return 'bg-white text-gray-600 border-2 border-gray-300 hover:bg-gray-50 hover:border-orange-400 hover:shadow-lg hover:shadow-orange-500/20'
    }
  }

  const getStatementStyle = (statementId: string) => {
    if (!showResults) {
      // Normal quiz mode - apply dark theme when fullscreen
      return isFullscreen
        ? 'bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-500 hover:border-slate-400 hover:shadow-lg'
        : 'bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 hover:border-slate-300 hover:shadow-lg'
    }

    const userAnswer = currentAnswers[statementId]
    if (userAnswer === undefined) {
      return isFullscreen
        ? 'bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-500'
        : 'bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200'
    }

    // For results view, we need to determine if the statement answer was correct
    // This would typically come from the statement's correctAnswer property
    const statement = statements.find(s => s.id === statementId)
    const isCorrect = statement?.correctAnswer === userAnswer

    return isCorrect
      ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-300 shadow-lg shadow-emerald-500/20'
      : 'bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 shadow-lg shadow-red-500/20'
  }

  // Chỉ sử dụng question_lead từ database, bỏ hoàn toàn cau_hoi
  const questionLeadContent = cleanStatementContent(question.question_lead || "")
  
  // Tách câu dẫn chính và instruction
  let mainQuestionContent = questionLeadContent
  let hasInstructionInContent = false
  
  // Kiểm tra xem content đã có instruction chưa
  const instructionPatterns = [
    /Xét tính đúng[,\s]*sai của các phát biểu sau/i,
    /Xét tính đúng[,\s]*sai/i,
    /Các phát biểu sau/i,
    /Phát biểu nào sau đây/i,
    /Câu nào.*đúng/i,
    /Câu nào.*sai/i
  ]
  
  // Kiểm tra xem có instruction trong content không
  for (const pattern of instructionPatterns) {
    if (pattern.test(questionLeadContent)) {
      hasInstructionInContent = true
      break
    }
  }
  
  // Bỏ hoàn toàn instruction riêng - không hiển thị nữa
  const showSeparateInstruction = false

  return (
    <div className="space-y-1.5 md:space-y-2 w-full max-w-full overflow-hidden">
      {/* 1. Câu dẫn (Question Lead) - Từ question_lead trong DS schema */}
      {mainQuestionContent && (
        <div className={`p-2 sm:p-2.5 md:p-3 rounded-lg md:rounded-xl border-2 shadow-lg group relative ${
          isFullscreen 
            ? 'bg-gradient-to-r from-slate-700 to-slate-600 border-slate-500' 
            : 'bg-gradient-to-r from-blue-50 via-sky-50 to-blue-100 border-blue-200 shadow-blue-500/10'
        }`}>
          <div className={`font-medium text-lg md:text-lg leading-relaxed ${
            isFullscreen ? 'text-slate-200' : 'text-gray-800'
          }`}>
            <KaTeXRenderer content={mainQuestionContent} moveImagesToEnd />
          </div>
          
          {/* AI Button - CHỈ hiển thị ở chế độ xem lại (showResults = true) */}
          {showResults && (
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <AIButton
                content={question}
                contentType="quiz"
                size="sm"
                variant="outline"
                title="Giải thích câu hỏi đúng/sai"
                className="bg-white border-blue-300 text-blue-600 hover:bg-blue-50"
                questionId={question.id}
                useDatabase={true}
              />
            </div>
          )}
        </div>
      )}

      {/* 2. Hình ảnh (nếu có) - Hiển thị sau câu dẫn */}
      {question.hinh_anh && (
        <div className="w-full overflow-hidden flex justify-center">
          <div className="p-1.5 sm:p-2 md:p-2.5 rounded-lg md:rounded-xl shadow-lg border-2 w-full max-w-full box-border bg-white border-gray-200" style={{ maxWidth: '100%' }}>
            <img
              src={question.hinh_anh}
              alt="Hình ảnh câu hỏi"
              className="w-full h-auto max-h-[180px] sm:max-h-[200px] md:max-h-[220px] rounded-lg border border-gray-300 mx-auto block object-contain"
              style={{ maxWidth: '100%', width: 'auto', display: 'block' }}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          </div>
        </div>
      )}

      {/* 3. 4 phát biểu từ database (statement_a/b/c/d) */}
      <div className="space-y-1 md:space-y-1">
        {statements.map((statement, index) => (
          <div
            key={statement.id}
            className={`p-1.5 sm:p-2 md:p-2 rounded-lg md:rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${getStatementStyle(statement.id)}`}
          >
            {/* Statement Content and True/False Buttons */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              {/* Statement text */}
              <div className="flex items-start space-x-2 md:space-x-3 flex-1 mb-1 md:mb-0">
                <span className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg shadow-orange-400/20 border border-orange-300">
                  {String.fromCharCode(65 + index)}
                </span>
                <div className={`flex-1 font-medium text-base md:text-base leading-relaxed ${
                  isFullscreen ? 'text-slate-200' : 'text-gray-800'
                }`}>
                  <KaTeXRenderer content={statement.content} />
                </div>
              </div>

              {/* True/False Buttons - Mobile: Horizontal centered below, Desktop: Horizontal on the right */}
              <div className="flex items-center justify-center space-x-3 md:justify-start md:space-x-3 md:ml-3">
                {/* True Button */}
                <button
                  onClick={() => !showResults && handleStatementAnswer(statement.id, true)}
                  disabled={showResults}
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 transition-all duration-300 font-bold text-sm shadow-md flex flex-col items-center justify-center ${getButtonStyle(statement.id, true, statement.correctAnswer)
                    } ${!showResults ? 'hover:scale-105 cursor-pointer active:scale-95' : 'cursor-default'}`}
                  title="Đúng"
                >
                  <Check className="w-2.5 h-2.5 md:w-3 md:h-3 mb-0.5" />
                  <span className="text-sm font-medium leading-none">Đúng</span>
                </button>

                {/* False Button */}
                <button
                  onClick={() => !showResults && handleStatementAnswer(statement.id, false)}
                  disabled={showResults}
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 transition-all duration-300 font-bold text-sm shadow-md flex flex-col items-center justify-center ${getButtonStyle(statement.id, false, statement.correctAnswer)
                    } ${!showResults ? 'hover:scale-105 cursor-pointer active:scale-95' : 'cursor-default'}`}
                  title="Sai"
                >
                  <X className="w-2.5 h-2.5 md:w-3 md:h-3 mb-0.5" />
                  <span className="text-sm font-medium leading-none">Sai</span>
                </button>
              </div>
            </div>

            {/* Result and Solution for Review Mode */}
            {showResults && (
              <div className="mt-2 md:mt-3 space-y-1.5 md:space-y-2">
                {/* Correct Answer Indicator */}
                <div className={`flex items-center space-x-2 text-sm md:text-base font-medium p-1.5 md:p-2 rounded-lg border-2 ${
                  statement.correctAnswer ? 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border-emerald-400 shadow-md' : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-400 shadow-md'
                }`}>
                  {statement.correctAnswer ? (
                    <Check className="w-3 h-3 md:w-4 md:h-4 text-emerald-600" />
                  ) : (
                    <X className="w-3 h-3 md:w-4 md:h-4 text-red-600" />
                  )}
                  <span>
                    Đáp án đúng: {statement.correctAnswer ? 'ĐÚNG' : 'SAI'}
                  </span>
                </div>

                {/* User Answer Result */}
                {currentAnswers[statement.id] !== undefined && (
                  <div className={`flex items-center space-x-2 text-sm md:text-base font-medium p-1.5 md:p-2 rounded-lg border-2 ${
                    statement.correctAnswer === currentAnswers[statement.id] 
                      ? 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border-emerald-400 shadow-md' 
                      : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-400 shadow-md'
                  }`}>
                    {statement.correctAnswer === currentAnswers[statement.id] ? (
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-emerald-600" />
                    ) : (
                      <X className="w-3 h-3 md:w-4 md:h-4 text-red-600" />
                    )}
                    <span>
                      Bạn chọn: {currentAnswers[statement.id] ? 'ĐÚNG' : 'SAI'} - {
                        statement.correctAnswer === currentAnswers[statement.id] 
                          ? '🎉 CHÍNH XÁC!' 
                          : '❌ SAI RỒI!'
                      }
                    </span>
                  </div>
                )}

                {/* Solution for this statement */}
                {(() => {
                  // Extract statement key from statement id (e.g., "123_statement_a" -> "a")
                  const statementKey = statement.id.split('_').pop() // a, b, c, d
                  const solutionKey = `solution_${statementKey}` // solution_a, solution_b, etc.
                  const solution = (question as any)[solutionKey]
                  
                  if (solution && solution.trim()) {
                    return (
                      <div className="bg-gradient-to-r from-orange-50 to-amber-100 border-2 border-orange-300 rounded-lg md:rounded-xl p-2 md:p-4 shadow-lg">
                        <div className="text-base md:text-xl font-bold text-orange-800 mb-1 md:mb-2 flex items-center">
                          <span className="mr-2">💡</span>
                          Lời giải phát biểu {String.fromCharCode(65 + index)}:
                        </div>
                        <div className="text-base md:text-xl text-orange-700">
                          <KaTeXRenderer content={solution} />
                        </div>
                      </div>
                    )
                  }
                  return null
                })()}
              </div>
            )}
          </div>
        ))}
      </div>

    </div>
  )
}

export default TrueFalseQuestion